import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/lib/utils-app";
import { ShieldCheck, HardHat, AlertTriangle, ScanFace, MapPin, CalendarDays, GitCompareArrows, Camera, Download } from "lucide-react";
import { StatTile, StatStrip } from "@/components/StatTile";
import ComparisonTrailDialog from "@/components/admin/ComparisonTrailDialog";

function istToday() {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

function ThumbCell({ cap }) {
  if (!cap) {
    return (
      <div className="mx-auto grid h-9 w-9 place-items-center rounded-md border border-dashed bg-muted/40 text-muted-foreground/50">
        <ScanFace className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img
      src={cap.photo_b64?.startsWith("data:") ? cap.photo_b64 : `data:image/jpeg;base64,${cap.photo_b64}`}
      alt=""
      className="mx-auto h-9 w-9 rounded-md border object-cover"
    />
  );
}

function PctBadge({ cap }) {
  if (!cap) return <span className="text-slate-400">—</span>;
  const pct = cap.face_match_pct;
  const cls =
    cap.face_status === "match"
      ? "text-emerald-700"
      : cap.face_status === "mismatch"
      ? "text-rose-700"
      : "text-amber-700";
  return (
    <span className={`font-mono text-xs font-semibold tabular-nums ${cls}`}>
      {pct == null ? "—" : `${pct}%`}
    </span>
  );
}

function CapsCell({ caps }) {
  if (!caps || caps.length === 0) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {caps.map((c) => (
        <span
          key={c.id}
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
        >
          <Camera className="h-3 w-3" />
          {c.window_label || "Open"} · {fmtTime(c.created_at)}
        </span>
      ))}
    </div>
  );
}

export default function AttendanceComparePage() {
  const [parks, setParks] = useState([]);
  const [parkId, setParkId] = useState("");
  const [date, setDate] = useState(istToday());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  // When set, opens the "past-3-days side-by-side captures" dialog. The
  // string is the role tile the admin clicked ("pmc" or "contractor"),
  // used to highlight the corresponding column in the dialog cells.
  const [trailRole, setTrailRole] = useState(null);

  useEffect(() => {
    api.get("/parks").then((r) => {
      setParks(r.data);
      if (!parkId && r.data[0]) setParkId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!parkId) return;
    setBusy(true);
    api
      .get("/admin/attendance/compare", { params: { park_id: parkId, date } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [parkId, date]);

  const flagBadge = (flag) => {
    if (!flag) return null;
    const map = {
      missing_pmc: { label: "Missing PMC", cls: "bg-rose-600 hover:bg-rose-600 text-white" },
      missing_contractor: { label: "Missing Contractor", cls: "bg-amber-500 hover:bg-amber-500 text-white" },
      score_mismatch: { label: "Score mismatch", cls: "bg-violet-600 hover:bg-violet-600 text-white" },
    };
    const m = map[flag];
    return (
      <Badge className={m.cls} data-testid={`att-flag-${flag}`}>
        <AlertTriangle className="mr-1 h-3 w-3" /> {m.label}
      </Badge>
    );
  };

  // Compute compare stats
  const totals = (data?.rows || []).reduce(
    (acc, r) => {
      acc.workers += 1;
      if (r.pmc && r.pmc.length > 0) acc.pmc_present += 1;
      if (r.contractor && r.contractor.length > 0) acc.con_present += 1;
      if (r.flag) acc.flagged += 1;
      return acc;
    },
    { workers: 0, pmc_present: 0, con_present: 0, flagged: 0 },
  );
  const park = parks.find((p) => p.id === parkId);

  const exportXlsx = async () => {
    if (!parkId) return;
    try {
      const { tokenStore, API_BASE } = await import("@/lib/api");
      const tk = tokenStore.get();
      const res = await fetch(`${API_BASE}/admin/attendance/compare-export.xlsx?park_id=${parkId}&date=${date}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `capture_compare_${park?.code || "park"}_${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {}
  };

  return (
    <AppShell title="PMC ↔ Contractor Comparison" subtitle="Side-by-side capture comparison for the selected park & date" action={
      <Button
        onClick={exportXlsx}
        disabled={!parkId}
        className="bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="compare-export-xlsx"
      >
        <Download className="mr-1.5 h-4 w-4" />
        Download Excel
      </Button>
    }>
      <div className="flex flex-col gap-4">
        <Card>
          <CardContent className="grid gap-3 p-4 md:grid-cols-12">
            <div className="md:col-span-7 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <MapPin className="mr-1 inline h-3 w-3" /> Park
              </Label>
              <Select value={parkId} onValueChange={setParkId}>
                <SelectTrigger data-testid="compare-park-select" className="h-10"><SelectValue placeholder="Select park" /></SelectTrigger>
                <SelectContent>
                  {parks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-5 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarDays className="mr-1 inline h-3 w-3" /> Date
              </Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="compare-date-input" className="h-10" />
            </div>
          </CardContent>
        </Card>

        <StatStrip cols={4}>
          <StatTile icon={GitCompareArrows} accent="slate" label="Workers" value={totals.workers} hint={park?.name} testid="compare-stat-workers" />
          <StatTile
            icon={ShieldCheck}
            accent="sky"
            label="PMC Present"
            value={totals.pmc_present}
            hint="Click for 3-day side-by-side"
            testid="compare-stat-pmc"
            onClick={() => setTrailRole("pmc")}
          />
          <StatTile
            icon={HardHat}
            accent="amber"
            label="Contractor Present"
            value={totals.con_present}
            hint="Click for 3-day side-by-side"
            testid="compare-stat-contractor"
            onClick={() => setTrailRole("contractor")}
          />
          <StatTile icon={AlertTriangle} accent="rose" label="Flagged" value={totals.flagged} hint="Mismatches" testid="compare-stat-flagged" />
        </StatStrip>

        {busy ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : !data || data.rows.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
              <GitCompareArrows className="h-8 w-8 opacity-40" />
              <div>No employees in this park.</div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="compare-rows">
                <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                  <tr className="border-b border-slate-200">
                    <th rowSpan={2} className="whitespace-nowrap px-2 py-2 text-center align-middle">#</th>
                    <th rowSpan={2} className="whitespace-nowrap px-2 py-2 text-center align-middle">Code</th>
                    <th rowSpan={2} className="whitespace-nowrap px-3 py-2 text-left align-middle">Worker</th>
                    <th colSpan={3} className="whitespace-nowrap border-l border-slate-200 px-2 py-2 text-center text-sky-700">
                      <ShieldCheck className="mr-1 inline h-3 w-3" /> ITCOT / PMC
                    </th>
                    <th colSpan={3} className="whitespace-nowrap border-l border-slate-200 px-2 py-2 text-center text-amber-700">
                      <HardHat className="mr-1 inline h-3 w-3" /> Contractor
                    </th>
                    <th rowSpan={2} className="whitespace-nowrap border-l border-slate-200 px-2 py-2 text-center align-middle">Flag</th>
                  </tr>
                  <tr className="border-b border-slate-200 text-[10px]">
                    <th className="whitespace-nowrap border-l border-slate-200 px-2 py-1.5 text-center">Photo</th>
                    <th className="whitespace-nowrap px-2 py-1.5 text-left">Captures</th>
                    <th className="whitespace-nowrap px-2 py-1.5 text-center">Match %</th>
                    <th className="whitespace-nowrap border-l border-slate-200 px-2 py-1.5 text-center">Photo</th>
                    <th className="whitespace-nowrap px-2 py-1.5 text-left">Captures</th>
                    <th className="whitespace-nowrap px-2 py-1.5 text-center">Match %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, idx) => {
                    const pmcCap = (r.pmc || [])[0];
                    const conCap = (r.contractor || [])[0];
                    const rowCls = r.flag
                      ? "bg-rose-50/60 hover:bg-rose-100/60 border-l-2 border-l-rose-300"
                      : "hover:bg-slate-50";
                    return (
                      <tr
                        key={r.employee_id}
                        className={`border-b border-slate-100 ${rowCls}`}
                        data-testid={`compare-row-${r.employee_code}`}
                      >
                        <td className="whitespace-nowrap px-2 py-2 text-center font-mono text-xs text-slate-500 tabular-nums">{idx + 1}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-center">
                          <span className="font-mono text-xs font-semibold text-slate-800">{r.employee_code}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-left font-medium text-slate-900">{r.employee_name}</td>
                        <td className="whitespace-nowrap border-l border-slate-100 px-2 py-2 text-center"><ThumbCell cap={pmcCap} /></td>
                        <td className="whitespace-nowrap px-2 py-2 text-left"><CapsCell caps={r.pmc} /></td>
                        <td className="whitespace-nowrap px-2 py-2 text-center"><PctBadge cap={pmcCap} /></td>
                        <td className="whitespace-nowrap border-l border-slate-100 px-2 py-2 text-center"><ThumbCell cap={conCap} /></td>
                        <td className="whitespace-nowrap px-2 py-2 text-left"><CapsCell caps={r.contractor} /></td>
                        <td className="whitespace-nowrap px-2 py-2 text-center"><PctBadge cap={conCap} /></td>
                        <td className="whitespace-nowrap border-l border-slate-100 px-2 py-2 text-center">
                          {flagBadge(r.flag) || <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
              <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-emerald-100 ring-1 ring-emerald-300" />Match</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-amber-100 ring-1 ring-amber-400" />Pending review</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-rose-100 ring-1 ring-rose-400" />Flagged / missing</span>
              <span className="ml-auto text-slate-500">PMC and Contractor captures side-by-side</span>
            </div>
          </Card>
        )}
      </div>

      {trailRole ? (
        <ComparisonTrailDialog
          park={park}
          date={date}
          days={3}
          defaultRole={trailRole}
          onClose={() => setTrailRole(null)}
        />
      ) : null}
    </AppShell>
  );
}
